import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Button,
  Badge,
  Group,
  Stack,
  Card,
  Select,
  Alert,
  Tooltip,
  Modal,
  ScrollArea,
  Skeleton,
  Pagination,
  ActionIcon,
} from '@mantine/core';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { formatDistanceToNow } from 'date-fns';
import {
  IconAlertCircle,
  IconClipboardList,
  IconRotateClockwise,
  IconChartBar,
  IconClock,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { api } from '@/services/api';
import { ServiceHealthWarning } from '@/components/ServiceHealthWarning';
import { JobTable, getJobTypeLabel } from '@/components/JobTable';
import { AutoRefreshControls } from '@/components/AutoRefreshControls';
import { useAutoRefresh } from '@/hooks/useAutoRefresh';

interface QueueJob {
  id: string;
  job_type: string;
  env_id: string;
  env_name?: string;
  namespace?: string;
  user_id: string;
  user_display_name?: string;
  status: string;
  queued_at: string;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
  correlation_id?: string;
}

interface QueueMetrics {
  status_counts: Array<{ status: string; count: number; job_type: string }>;
  oldest_queued: any;
  max_concurrency: number;
}

export function AdminQueuePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ job_type: '', status: '' });
  const { enabled: autoRefresh, setEnabled: setAutoRefresh, refetchInterval } = useAutoRefresh({ interval: 5000 });
  const [retryModalOpened, setRetryModalOpened] = useState(false);
  const [selectedJob, setSelectedJob] = useState<QueueJob | null>(null);

  const limit = 50;

  // Helper functions
  const safeFormatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return 'Invalid date';
    }
  };


  // Fetch jobs
  const {
    data: jobsData,
    isLoading: isLoadingJobs,
    error: jobsError,
    refetch: refetchJobs,
  } = useQuery({
    queryKey: ['adminQueue', filters, page],
    queryFn: async () => {
      return await api.getAdminQueueHistory({
        limit,
        offset: (page - 1) * limit,
        ...(filters.job_type && { job_type: filters.job_type }),
        ...(filters.status && { status: filters.status }),
      });
    },
    refetchInterval,
  });

  // Fetch metrics
  const {
    data: metricsData,
    isLoading: isLoadingMetrics,
  } = useQuery({
    queryKey: ['adminQueueMetrics'],
    queryFn: async () => await api.getAdminQueueMetrics(),
    refetchInterval,
  });

  // Retry mutation
  const retryMutation = useMutation({
    mutationFn: async (envId: string) => {
      return await api.retryEnvironment(envId);
    },
    onSuccess: () => {
      notifications.show({
        title: 'Job Retried',
        message: 'The job has been queued for retry',
        color: 'green',
      });
      setRetryModalOpened(false);
      setSelectedJob(null);
      refetchJobs();
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Retry Failed',
        message: error.message || 'Failed to retry job',
        color: 'red',
      });
    },
  });

  const handleRetry = () => {
    if (selectedJob?.env_id) {
      retryMutation.mutate(selectedJob.env_id);
    }
  };

  const jobs: QueueJob[] = jobsData?.data || [];
  const totalCount = jobsData?.count || 0;
  const metrics: QueueMetrics | null = metricsData?.data || null;

  // Calculate metrics
  const queuedCount = metrics?.status_counts
    ?.filter(s => s.status === 'queued')
    ?.reduce((sum, s) => sum + s.count, 0) || 0;
  
  const processingCount = metrics?.status_counts
    ?.filter(s => s.status === 'processing')
    ?.reduce((sum, s) => sum + s.count, 0) || 0;

  const failedCount = jobs.filter(j => j.status === 'failed').length;
  const maxConcurrency = metrics?.max_concurrency || 5;

  if (jobsError) {
    return (
      <Container mt="md">
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          Failed to load queue data: {(jobsError as Error).message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="md">
        <Stack gap={5}>
          <Title order={1}>Admin Queue</Title>
          <Text c="dimmed">Monitor and manage job queue operations</Text>
        </Stack>
        <AutoRefreshControls
          enabled={autoRefresh}
          onEnabledChange={setAutoRefresh}
          intervalLabel="(5s)"
          onManualRefresh={refetchJobs}
          isRefreshing={isLoadingJobs}
        />
      </Group>

      <ServiceHealthWarning
        services={['queue']}
        message="Job processing may be impacted"
      />

      {/* Metrics Cards */}
      {isLoadingMetrics ? (
        <Group grow mb="xl">
          <Skeleton height={100} />
          <Skeleton height={100} />
          <Skeleton height={100} />
          <Skeleton height={100} />
        </Group>
      ) : (
        <Group grow mb="xl">
          <Card withBorder p="md">
            <Group justify="apart" mb="xs">
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Queue Depth
              </Text>
              <IconClipboardList size={20} style={{ opacity: 0.5 }} />
            </Group>
            <Text size="xl" fw={700}>
              {queuedCount}
            </Text>
            <Badge color="blue" size="sm" mt="xs">
              Queued
            </Badge>
          </Card>

          <Card withBorder p="md">
            <Group justify="apart" mb="xs">
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Processing
              </Text>
              <IconChartBar size={20} style={{ opacity: 0.5 }} />
            </Group>
            <Text size="xl" fw={700}>
              {processingCount}
            </Text>
            <Badge color="yellow" size="sm" mt="xs">
              Active
            </Badge>
          </Card>

          <Card withBorder p="md">
            <Group justify="apart" mb="xs">
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Failed
              </Text>
              <IconAlertTriangle size={20} style={{ opacity: 0.5 }} />
            </Group>
            <Text size="xl" fw={700}>
              {failedCount}
            </Text>
            <Badge color="red" size="sm" mt="xs">
              Needs Attention
            </Badge>
          </Card>

          <Card withBorder p="md">
            <Group justify="apart" mb="xs">
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                Max Concurrency
              </Text>
              <IconClock size={20} style={{ opacity: 0.5 }} />
            </Group>
            <Text size="xl" fw={700}>
              {maxConcurrency}
            </Text>
            <Badge color="gray" size="sm" mt="xs">
              Workers
            </Badge>
          </Card>
        </Group>
      )}

      {/* Filters */}
      <Group mb="md">
        <Select
          label="Job Type"
          placeholder="All types"
          clearable
          data={[
            { value: 'create_environment', label: 'Create Environment' },
            { value: 'delete_environment', label: 'Delete Environment' },
            { value: 'create_database', label: 'Create Database' },
            { value: 'delete_database', label: 'Delete Database' },
            { value: 'health_check', label: 'Health Check' },
            { value: 'fetch_resources', label: 'Fetch Resources' },
            { value: 'get_database_status', label: 'Get Database Status' },
          ]}
          value={filters.job_type}
          onChange={(value) => {
            setFilters({ ...filters, job_type: value || '' });
            setPage(1);
          }}
          style={{ minWidth: 200 }}
        />

        <Select
          label="Status"
          placeholder="All statuses"
          clearable
          data={[
            { value: 'queued', label: 'Queued' },
            { value: 'processing', label: 'Processing' },
            { value: 'completed', label: 'Completed' },
            { value: 'failed', label: 'Failed' },
          ]}
          value={filters.status}
          onChange={(value) => {
            setFilters({ ...filters, status: value || '' });
            setPage(1);
          }}
          style={{ minWidth: 150 }}
        />
      </Group>

      {/* Jobs Table */}
      {isLoadingJobs ? (
        <Stack>
          <Skeleton height={60} />
          <Skeleton height={400} />
        </Stack>
      ) : jobs.length === 0 ? (
        <Card withBorder p="xl" radius="md" ta="center">
          <Stack align="center">
            <IconClipboardList size={48} style={{ opacity: 0.5 }} />
            <Title order={3}>No Jobs Found</Title>
            <Text c="dimmed">
              {filters.job_type || filters.status
                ? 'Try adjusting your filters'
                : 'The queue is currently empty'}
            </Text>
          </Stack>
        </Card>
      ) : (
        <Card withBorder radius="md" p={0}>
          <ScrollArea>
            <JobTable 
              jobs={jobs}
              showEnvColumn={true}
              showUserColumn={true}
              onNavigate={(job) => {
                if (job.job_type.includes('environment')) {
                  navigate(`/environments/${job.env_id}`);
                } else if (job.job_type.includes('database')) {
                  navigate(`/databases/${job.env_id}`);
                }
              }}
              actionColumn={(job) => (
                job.status === 'failed' && job.job_type.includes('environment') ? (
                  <Tooltip label="Retry Job">
                    <ActionIcon
                      variant="subtle"
                      color="blue"
                      onClick={() => {
                        setSelectedJob(job as QueueJob);
                        setRetryModalOpened(true);
                      }}
                      aria-label="Retry job"
                    >
                      <IconRotateClockwise size={18} />
                    </ActionIcon>
                  </Tooltip>
                ) : null
              )}
            />
          </ScrollArea>
        </Card>
      )}

      {/* Pagination */}
      {totalCount > limit && (
        <Group justify="center" mt="xl">
          <Pagination
            total={Math.ceil(totalCount / limit)}
            value={page}
            onChange={setPage}
          />
        </Group>
      )}

      {/* Retry Confirmation Modal */}
      <Modal
        opened={retryModalOpened}
        onClose={() => {
          setRetryModalOpened(false);
          setSelectedJob(null);
        }}
        title="Retry Job"
        centered
      >
        <Text size="sm">
          Are you sure you want to retry this job?
        </Text>
        {selectedJob && (
          <>
            <Text size="sm" c="dimmed" mt="xs">
              Job ID: {selectedJob.id}
            </Text>
            <Text size="sm" c="dimmed">
              Type: {getJobTypeLabel(selectedJob.job_type)}
            </Text>
            {selectedJob.env_name && (
              <Text size="sm" c="dimmed">
                Environment: {selectedJob.env_name}
              </Text>
            )}
          </>
        )}
        <Group justify="flex-end" mt="md">
          <Button
            variant="default"
            onClick={() => {
              setRetryModalOpened(false);
              setSelectedJob(null);
            }}
          >
            Cancel
          </Button>
          <Button
            color="blue"
            onClick={handleRetry}
            loading={retryMutation.isPending}
          >
            Retry Job
          </Button>
        </Group>
      </Modal>
    </Container>
  );
}

